import React, { useState, useCallback, useMemo } from 'react';
import WizardStepLayout from '../WizardStepLayout';
import { WIZARD_ACTIONS } from '../constants';
import { getAdditionalFields } from '../../utils/formFieldBuckets';
import { useFilter } from '../../FilterContext';
import TaxonomyPicker from '../../TaxonomyPicker';

const AdditionalInfoStep = ({ state, dispatch, onNext, onBack }) => {
  const [expanded, setExpanded] = useState(false);

  const additionalFields = getAdditionalFields(state.formFields || []);

  // Chip catalogue for the active project. Reuses the same fetch the
  // FilterBar uses, so the wizard never triggers a second network round
  // trip for the same data. When the project has no chips configured
  // (legacy single-form sites), the picker hides itself entirely. We
  // only need the catalogue here to decide whether to render the picker
  // wrapper at all; TaxonomyPicker re-reads it from context itself.
  const { taxonomies } = useFilter();
  const hasChipCatalogue = useMemo(
    () => (taxonomies || []).length > 0,
    [taxonomies],
  );

  const themeRanks = useMemo(
    () => state.themeRanks || { 1: null, 2: null, 3: null },
    [state.themeRanks],
  );
  const propertyTypeRanks = useMemo(
    () => state.propertyTypeRanks || { 1: null, 2: null, 3: null },
    [state.propertyTypeRanks],
  );
  const communityRanks = useMemo(
    () => state.communityRanks || { 1: null, 2: null, 3: null },
    [state.communityRanks],
  );

  const handleChange = useCallback(
    (fieldId, value) => {
      dispatch({
        type: WIZARD_ACTIONS.SET_ADDITIONAL_FIELD,
        payload: { fieldId, value },
      });
    },
    [dispatch],
  );

  // TaxonomyPicker signals intent; we map it onto wizard reducer actions.
  // id === null means "clear this rank" (reducer compacts higher ranks).
  // Client July 2026 (#7): all three kinds are ranked, so each gets the
  // same set/clear-at-rank dispatch pair.
  const handlePickThemeAtRank = useCallback(
    (rank, themeId) => {
      if (themeId == null) {
        dispatch({ type: WIZARD_ACTIONS.CLEAR_THEME_AT_RANK, payload: { rank } });
      } else {
        dispatch({
          type: WIZARD_ACTIONS.SET_THEME_AT_RANK,
          payload: { rank, id: themeId },
        });
      }
    },
    [dispatch],
  );

  const handlePickPropertyTypeAtRank = useCallback(
    (rank, ptId) => {
      if (ptId == null) {
        dispatch({
          type: WIZARD_ACTIONS.CLEAR_PROPERTY_TYPE_AT_RANK,
          payload: { rank },
        });
      } else {
        dispatch({
          type: WIZARD_ACTIONS.SET_PROPERTY_TYPE_AT_RANK,
          payload: { rank, id: ptId },
        });
      }
    },
    [dispatch],
  );

  const handlePickCommunityAtRank = useCallback(
    (rank, communityTaxonomyId) => {
      if (communityTaxonomyId == null) {
        dispatch({
          type: WIZARD_ACTIONS.CLEAR_COMMUNITY_AT_RANK,
          payload: { rank },
        });
      } else {
        dispatch({
          type: WIZARD_ACTIONS.SET_COMMUNITY_AT_RANK,
          payload: { rank, id: communityTaxonomyId },
        });
      }
    },
    [dispatch],
  );

  const hasFields = additionalFields.length > 0;

  return (
    <WizardStepLayout
      stepNumber={5}
      onNext={onNext}
      onBack={onBack}
      nextDisabled={false}
      nextLabel="Next →"
      showBack
      hideSubtitle
    >
      {hasChipCatalogue && (
        <div className="wizard__chip-picker">
          <p className="wizard__helper-text wizard__chip-picker-intro">
            Tag this place with categories so visitors can find it through
            filters. <span className="wizard__optional-tag">(optional)</span>
          </p>
          <TaxonomyPicker
            themeRanks={themeRanks}
            propertyTypeRanks={propertyTypeRanks}
            communityRanks={communityRanks}
            onPickThemeAtRank={handlePickThemeAtRank}
            onPickPropertyTypeAtRank={handlePickPropertyTypeAtRank}
            onPickCommunityAtRank={handlePickCommunityAtRank}
          />
        </div>
      )}

      {hasFields ? (
        <>
          <button
            type="button"
            className="wizard__step5-toggle"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <span className="wizard__step5-toggle-text">
              Add any extra details about this place{' '}
              <span className="wizard__optional-tag">(optional)</span>
            </span>
            <svg
              className={`wizard__chevron wizard__chevron--step5${expanded ? ' wizard__chevron--open' : ''}`}
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {expanded && (
            <div className="wizard__accordion-body wizard__accordion-body--step5">
              {additionalFields.map((field) => {
                const value = state.additionalFields[field.field_id] || '';
                const t = String(field.field_type || '').toLowerCase();
                // Render by the field_type returned from FormHandler.php.
                // `text` and `textarea` both render as multiline boxes
                // matching StoryStep — projects routinely store prose-y
                // extras (Occupant, Artifacts, Notes) as `text`, so
                // single-line is too cramped to be useful.
                let input;
                if (t === 'number') {
                  input = (
                    <input
                      className="wizard__input"
                      type="number"
                      placeholder={field.field_name}
                      value={value}
                      onChange={(e) => handleChange(field.field_id, e.target.value)}
                    />
                  );
                } else if (t === 'date') {
                  input = (
                    <input
                      className="wizard__input"
                      type="date"
                      value={value}
                      onChange={(e) => handleChange(field.field_id, e.target.value)}
                    />
                  );
                } else {
                  input = (
                    <textarea
                      className="wizard__textarea"
                      rows={4}
                      placeholder={field.field_name}
                      value={value}
                      onChange={(e) => handleChange(field.field_id, e.target.value)}
                    />
                  );
                }
                return (
                  <div key={field.field_id}>
                    <label className="wizard__helper-text" style={{ display: 'block', marginBottom: 4 }}>
                      {field.field_name}
                    </label>
                    {input}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        // Suppress the "no additional fields" copy when the chip picker
        // already filled the step with content. Showing both reads as a
        // contradiction ("here are some categories... and also nothing
        // is configured"), and the chip picker IS the additional info
        // for chip-only projects.
        !hasChipCatalogue && (
          <p className="wizard__helper-text">
            No additional fields are configured for this project.
          </p>
        )
      )}
    </WizardStepLayout>
  );
};

export default React.memo(AdditionalInfoStep);
